<template>
  <div>
    <menu-content-container
      :chip="$t('forms.gif_properties')"
      chip-secondary
      border-full
    >
      <content-row
        style="display: block; justify-content: center"
        :margin-bottom="16"
        :margin-top="16"
      >
        <TTabsV2
          data-testid="gif-tabs"
          :options="sourceOptionsNavigation"
          @on-tab-change="onSourceTabChanged"
        ></TTabsV2>
      </content-row>

      <content-row v-if="dataSource === 'giphy'">
        <div style="width: 100%">
          <t-input
            v-model="queryComp"
            data-testid="gif-gipfy-search-query"
            :placeholder="$t('forms.search_gifs')"
            @focus="search"
          ></t-input>
          <ul v-show="gifs.length > 0" id="gifs" class="gifs my-4">
            <li
              v-for="(gif, i) in gifs"
              :key="i"
              class="gif"
              @click="selectGif(gif)"
            >
              <img :src="gif.images.fixed_height_small.url" class="inline" />
            </li>
          </ul>
          <ul v-show="gifs.length === 0" class="gifs my-4">
            <li class="gif placeholder">
              <img :src="src" class="inline max-h-[100px]" />
            </li>
          </ul>
          <img
            src="https://d70shl7vidtft.cloudfront.net/editor/powered_by_black_bg_27.png"
            style="width: 45px"
          />
        </div>
      </content-row>

      <content-row v-if="dataSource === 'custom'">
        <t-input
          v-model="src"
          :label="$t('forms.image_url')"
          data-testid="gif-custom-source-src"
          @on-blur="mutationEmptyString = ''"
        ></t-input>
      </content-row>

      <content-row
        v-if="
          dataSource === 'custom' &&
          fileManagerStore.customFileManager &&
          fileManagerStore.enableFileManagerInGif
        "
        :margin-top="16"
      >
        <ImageButtons v-model="src" show-replace :show-edit="false" />
      </content-row>
      <content-row>
        <t-auto-complete
          v-model="href"
          :label="$t('forms.image_link')"
          data-testid="gif-link"
          placeholder="https://"
          :has-icon="true"
          :flat-merge-tags="mergeTagsStore.flatMergeTags"
          :grouped-merge-tags="mergeTagsStore.mergeTagsForInputs"
          :smart-merge-tags="mergeTagsStore.smartMergeTags"
          @on-blur="parseHref()"
        ></t-auto-complete>
      </content-row>
      <content-row>
        <t-auto-complete
          v-model="alt"
          :label="$t('forms.image_alt')"
          data-testid="gif-alt"
          :has-icon="true"
          :flat-merge-tags="mergeTagsStore.flatMergeTags"
          :grouped-merge-tags="mergeTagsStore.mergeTagsForInputs"
          :smart-merge-tags="mergeTagsStore.smartMergeTags"
          @on-blur="mutationEmptyString = ''"
        ></t-auto-complete>
      </content-row>
    </menu-content-container>

    <menu-content-container :header="$t('forms.image_size')" border-full>
      <content-row :margin-top="16">
        <t-slider
          v-model="width"
          :max="containerWidth"
          :min="30"
          unit="px"
          data-testid="gif-size-slider"
        ></t-slider>
      </content-row>
    </menu-content-container>

    <menu-content-container border-full>
      <TLabel
        :changed="align.changed"
        :tooltip="$t('forms.reset_value')"
        data-testid="align-label"
        @reset="align.reset"
        >{{ $t("forms.image_align") }}</TLabel
      >
      <content-row justify-between>
        <TButtonGroup>
          <TGroupButton
            data="left"
            small
            data-testid="align-left"
            :active="align.model === 'left'"
            @button-clicked="align.model = 'left'"
          >
            <AligmentLeft />
          </TGroupButton>
          <TGroupButton
            data="center"
            small
            data-testid="align-center"
            :active="align.model === 'center'"
            @button-clicked="align.model = 'center'"
          >
            <AligmentCenter />
          </TGroupButton>
          <TGroupButton
            data="right"
            small
            data-testid="align-right"
            :active="align.model === 'right'"
            @button-clicked="align.model = 'right'"
          >
            <AligmentRight />
          </TGroupButton>
        </TButtonGroup>
        <TSwitch
          v-model="fluidOnMobile.model"
          data-testid="fluid-on-mobile"
          :label="$t('forms.fluid_on_mobile')"
          :changed="fluidOnMobile.changed"
          :reset-tooltip="$t('forms.reset_value')"
          @reset="fluidOnMobile.reset"
        />
      </content-row>
    </menu-content-container>

    <borderRadiusForm
      data-testid="image-border-radius"
      :label="$t('forms.border_radius')"
      :min-y="0"
      :max-y="containerWidth"
      :min-x="0"
      :max-x="containerWidth"
      :changed="borderRadius.changed"
      :default-value="borderRadius.defaultValue"
      @reset="borderRadius.reset"
    ></borderRadiusForm>

    <padding-form
      data-testid="gif-margin"
      :label="$t('forms.image_margin')"
      :changed="padding.changed"
      :default-value="padding.defaultValue"
      @reset="padding.reset"
    ></padding-form>

    <menu-content-container :border-bottom="false">
      <content-row>
        <hideOnMobile></hideOnMobile>
      </content-row>
      <content-row :margin-top="16" :margin-bottom="20">
        <hide-on-desktop></hide-on-desktop>
      </content-row>
    </menu-content-container>
  </div>
</template>

<script lang="ts" setup>
  import hideOnMobile from "./components/hideOnMobile.vue";
  import hideOnDesktop from "./components/hideOnDesktop.vue";
  import paddingForm from "./paddingForm.vue";
  import borderRadiusForm from "./borderRadiusForm.vue";
  import ImageButtons from "./components/ImageButtons/ImageButtons.vue";

  import { useMjmlStore } from "@/stores/mjmlStore";
  import { useMergeTagsStore } from "@/stores/mergeTags";
  import { useFileManagerStore } from "@/stores/fileManger";
  import { useBlockAttr } from "@/composables/useBlockAttr";
  import { lockedApi } from "../api";

  import { AligmentLeft, AligmentCenter, AligmentRight } from "@topol/icons";
  import {
    TInput,
    TButtonGroup,
    TGroupButton,
    TLabel,
    TSliderClassic as TSlider,
    TMergetagsAutocomplete as TAutoComplete,
    TSwitch,
    TTabsV2,
  } from "@topol/twig-ui";

  import { AxiosInstance, AxiosStatic } from "axios";
  import { useI18n } from "vue-i18n";
  import {
    ref,
    Ref,
    computed,
    getCurrentInstance,
    onMounted,
    onBeforeUnmount,
  } from "vue";

  const app = getCurrentInstance();

  if (app === null) {
    throw new Error("App is not defined");
  }

  const $http: AxiosStatic | AxiosInstance =
    app.appContext.config.globalProperties.$http;

  type Gif = {
    images: {
      fixed_height_small: {
        url: string;
      };
      downsized_medium: {
        url: string;
      };
    };
  };

  const query = ref("");
  const gifs: Ref<Gif[]> = ref([]);
  const timeout = ref(null);
  const dataSource = ref("giphy");
  const mutationEmptyString = ref("");

  const mergeTagsStore = useMergeTagsStore();
  const mjmlStore = useMjmlStore();
  const fileManagerStore = useFileManagerStore();
  const { t } = useI18n();
  const { str, bool, nullable } = useBlockAttr({ mode: "active" });

  // --- Composable bindings ---
  const align = str("align", "center");
  const fluidOnMobile = bool("fluid-on-mobile", "true", "false");
  const borderRadius = nullable("border-radius");
  const padding = nullable("padding");

  const src = computed({
    get() {
      if (mutationEmptyString.value === "src") {
        return "";
      }
      return mjmlStore.getActiveBlockAttrString("src");
    },
    set(val: string) {
      if ((!val || val.trim() === "") && mjmlStore.mjml.langs) {
        // If the value is empty, we set the mutationEmptyString to true
        // to avoid immediate display of the value from the primary mutation.
        mutationEmptyString.value = "src";
      } else {
        mutationEmptyString.value = "";
      }
      mjmlStore.updateBlockAttribute({
        prop: "src",
        value: val.trim(),
      });
    },
  });

  const queryComp = computed({
    get() {
      return query.value;
    },
    set(val: string) {
      query.value = val;
      search();
    },
  });

  const href = computed({
    get() {
      if (mutationEmptyString.value === "href") {
        return "";
      }
      return mjmlStore.getActiveBlockAttrString("href");
    },
    set(val: string) {
      if ((!val || val.trim() === "") && mjmlStore.mjml.langs) {
        // If the value is empty, we set the mutationEmptyString to true
        // to avoid immediate display of the value from the primary mutation.
        mutationEmptyString.value = "href";
      } else {
        mutationEmptyString.value = "";
      }
      mjmlStore.updateBlockAttribute({
        prop: "href",
        value: val.trim(),
      });
    },
  });

  const alt = computed({
    get() {
      if (mutationEmptyString.value === "alt") {
        return "";
      }
      return mjmlStore.getActiveBlockAttrString("alt");
    },
    set(val: string) {
      if ((!val || val.trim() === "") && mjmlStore.mjml.langs) {
        // If the value is empty, we set the mutationEmptyString to true
        // to avoid immediate display of the value from the primary mutation.
        mutationEmptyString.value = "alt";
      } else {
        mutationEmptyString.value = "";
      }
      mjmlStore.updateBlockAttribute({
        prop: "alt",
        value: val,
      });
    },
  });

  const containerWidth = computed(() => {
    return mjmlStore.getActiveBlockAttrInt("containerWidth");
  });

  const width = computed({
    get() {
      if (mjmlStore.getActiveBlockAttrInt("width")) {
        if (mjmlStore.getActiveBlockAttrInt("width") > containerWidth.value) {
          return containerWidth.value;
        }
        return mjmlStore.getActiveBlockAttrInt("width");
      } else {
        return containerWidth.value;
      }
    },
    set(val: number) {
      mjmlStore.updateBlockAttribute({
        prop: "width",
        value: val,
      });
      mjmlStore.updateBlockAttribute({
        prop: "widthPercent",
        value: parseInt(
          //@ts-expect-error
          (val / containerWidth.value) * 100
        ),
      });
    },
  });

  const sourceOptionsNavigation = computed(() => {
    interface ContentOptions {
      name: string;
      value: "giphy" | "custom";
      disabled?: boolean;
      tooltip?: string;
    }

    const options: ContentOptions[] = [
      {
        name: "GIPHY",
        value: "giphy",
        tooltip: "",
      },
      {
        name: `${t("editor.custom")} ${t("forms.source")}`,
        value: "custom",
        tooltip: "",
      },
    ];

    return options;
  });

  function parseHref() {
    mutationEmptyString.value = "";
    const syntax = mergeTagsStore.smartMergeTags?.syntax || {
      start: "*|",
      end: "|*",
    };
    const startMergeTagSyntax = syntax.start || "*|";
    const startsWithSpecialChar = /^[^a-zA-Z0-9]/.test(href.value);
    if (
      href.value &&
      !href.value.startsWith("mailto") &&
      !href.value.startsWith("tel") &&
      !href.value.startsWith(startMergeTagSyntax) &&
      !startsWithSpecialChar &&
      !href.value.startsWith("http")
    ) {
      href.value = "http://" + href.value;
    }
  }

  function search() {
    //@ts-expect-error
    clearTimeout(timeout.value);
    if (query.value.length > 1) {
      //@ts-expect-error
      timeout.value = setTimeout(() => {
        $http
          .get(lockedApi.GIPHY_SEARCH, {
            params: {
              api_key: lockedApi.GIPHY_API_KEY,
              q: query.value,
            },
          })
          .then((data) => {
            gifs.value = data.data.data;
          });
      }, 300);
    } else {
      gifs.value = [];
    }
  }

  function scroll(e: any) {
    const gifs = document.getElementById("gifs");
    if (gifs !== null) {
      gifs.scrollLeft = gifs.scrollLeft + e.deltaY;
    }
  }

  function selectGif(gif: Gif) {
    mjmlStore.updateBlockAttribute({
      prop: "src",
      value: gif.images.downsized_medium.url,
    });
    gifs.value = [];
  }

  function onSourceTabChanged(index: number) {
    dataSource.value = sourceOptionsNavigation.value[index].value;
  }

  onMounted(() => {
    const el = document.getElementById("gifs");
    el?.addEventListener("mousewheel", scroll);
  });

  onBeforeUnmount(() => {
    const element = document.getElementById("gifs");
    if (element) {
      element.removeEventListener("mousewheel", scroll);
    }
  });
</script>
